import { useMemo } from 'react';
import { usePlugins } from 'domains/reporter/RichTextEditor/hooks';
import { useMacros } from 'domains/reporter/Template/useMacros';
import type { EnhanceEditorState, SlateContent } from '../../types';
import { isMacro, type MacroLookupEntry } from './utils';
import { unfurlMacroPlaceholderStatic } from './useMacroPlaceholder';

export type MacroPreviewData = {
  macroName: string;
  macroContent: Nullable<SlateContent>;
};

// Depth-first search over nested macro trees to find the first matching smid.
const findMacroBySmid = (
  macros: ReadonlyArray<MacroLookupEntry>,
  smid: string
): Nullable<MacroLookupEntry> => {
  const stack = [...macros];

  while (stack.length > 0) {
    const macro = stack.pop();
    if (macro == null) {
      continue;
    }

    if (macro.smid === smid) {
      return macro;
    }

    if (macro.nestedMacros != null && macro.nestedMacros.length > 0) {
      stack.push(...macro.nestedMacros);
    }
  }

  return null;
};

export const computeMacroPreview = ({
  referenceID,
  macros,
  editorStateEnhancers,
}: {
  referenceID: string;
  macros: ReadonlyArray<MacroLookupEntry>;
  editorStateEnhancers: EnhanceEditorState[];
}): MacroPreviewData => {
  const macro = findMacroBySmid(macros, referenceID);
  const macroName = macro?.name ?? '';
  const macroContent = isMacro(macro?.text)
    ? unfurlMacroPlaceholderStatic(macro.text, macros, editorStateEnhancers, {
        fragmentMode: true,
      })
    : null;

  return {
    macroName,
    macroContent,
  };
};

export const useMacroPreview = (referenceID: Nullable<string>): Nullable<MacroPreviewData> => {
  const macros = useMacros();
  const { getEditorStateEnhancers } = usePlugins();

  const editorStateEnhancers = useMemo(() => getEditorStateEnhancers(), [getEditorStateEnhancers]);

  return useMemo(() => {
    if (referenceID == null) {
      return null;
    }

    return computeMacroPreview({
      referenceID,
      macros,
      editorStateEnhancers,
    });
  }, [referenceID, macros, editorStateEnhancers]);
};
